import { useQuery } from '@tanstack/react-query'

import axios from '@pepper/utils/src/function/axiosInstance'

import { DecodedDocFilterParams } from 'components/items/FilterParams'

type Props = {
  companyId: string
  pageSize: number
  queryParams?: DecodedDocFilterParams
  itemType?: string
  projectId?: string
  options?: { isEnabled?: boolean }
}

type ReturnType = {
  records: Item[]
  total: number
  isLoading: boolean
  refetch: () => void
}

export const useDocItems = ({
  companyId,
  pageSize,
  projectId,
  queryParams = {},
  options,
}: Props): ReturnType => {
  const page = queryParams?.currentPage ?? 0
  const { sort, search_title, status, assignee } = queryParams

  const _getQueryParams = () => {
    const params: Record<string, string | number | undefined> = {
      company_id: companyId,
      workflow_status_id: status,
      workflow_assignee_id: assignee,
    }

    if (!queryParams) return params

    // Set sort params
    if (sort?.sort_by) {
      params.sort_by = sort.sort_by
      params.sort_order = sort.sort_order
    }

    // Set search params
    if (search_title) {
      params.search_title = search_title
    }

    return params
  }

  const fetchDocItems = async (_projectId?: string) => {
    const _queryParams = _getQueryParams()

    const _params = {
      ..._queryParams,
      company_id: companyId,
      page_size: pageSize,
      current_page: page,
      item_types: 'private_doc',
      project_id: _projectId,
    }

    const { data: _data } = await axios.get('/items', {
      params: _params,
    })

    return _data
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['doc-items', projectId ?? 'all', queryParams],
    queryFn: () => fetchDocItems(projectId),
    enabled: options?.isEnabled,
  })

  return {
    records: data?.records ?? [],
    total: data?.total_records ?? 0,
    isLoading,
    refetch,
  }
}
